/* ═══════════════════════════════════════════
   § 3. CRM STATE & VARIABLES
   Single shared mutable state object.
   All modules import { state } from here and
   read/write its properties directly.
   THREE.js object properties (Vector3, Quaternion)
   are initialized as null and populated by
   scene-setup.js after the engine boots.
   ═══════════════════════════════════════════ */

// DRS values computed once from device pixel ratio (matches original § 9 logic)
const _nativeDPR = Math.min(window.devicePixelRatio, 2);

export const state = {

  // ── CRM data ──────────────────────────────
  statusMap: {},
  sectorMap: {},
  sectorColorMap: {},
  facingMap: {},
  crmData: [],
  uiSettings: {},
  config: {},

  // ── UI flags ──────────────────────────────
  showStatus: false,
  showSector: false,
  mapOn: false,
  selectedPlotId: null,
  waMsg: '',
  waNumber: '919999999999',

  // ── 3D mesh collections ───────────────────
  plotMeshes: new Map(),   // MatName → Array<Mesh>
  originalColors: new Map(), // uuid → hex string

  // ── Camera / scene positions ──────────────
  // (THREE.Vector3 instances — assigned by scene-setup.js)
  baseCenter: null,
  selectedPlotCenter: null,
  default3DPos: null,
  defaultTopPos: null,
  _maxDim: 300,

  // ── Camera animation state ────────────────
  // (THREE objects — assigned by scene-setup.js)
  animTarget: null,
  animCameraTarget: null,
  animTime: 0,
  animDurationScale: 1.2,
  startCamPos: null,
  startCtrlTarget: null,
  startCamQuat: null,
  endCamQuat: null,
  isAnimatingCamera: false,

  // ── View state ────────────────────────────
  is3DView: true,

  // ── Nearby connectivity ───────────────────
  locations: [],
  currentPath: null,
  currentPin: null,
  currentRipple: null,
  pathAnimFrame: null,

  // ── Audio handles ─────────────────────────
  bgmAudio: null,
  sfxAudio: null,
  skyDome: null,
  skyLoaded: false,

  // ── DRS (Dynamic Resolution Scaling) ──────
  _nativeDPR,
  _DRS_GRACE: 10,
  _DRS_FLOOR: Math.max(_nativeDPR * 0.75, 0.75),
  _drsFrames: 0,
  _drsElapsed: 0,
  _drsSamples: [],
  _drsLocked: false,

  // ── AQC (Adaptive Quality Cascade) ─────────
  // Emergency fallback for devices too weak to complete loading at standard quality.
  // Normal devices never touch these — the standard DRS floor (75%) is unchanged.
  _AQC_EMERGENCY_FLOOR: Math.max(_nativeDPR * 0.5, 0.5),
  _aqcActive: false,  // true only if loading triggered emergency reduction

  // ── Smart render idle sleep ────────────────
  _idleTimer: 0,
  _isIdle: false,
  IDLE_TIMEOUT: 60,

  // ── BGM lifecycle ─────────────────────────
  _bgmWasPlaying: false,
  _bgmSavedVolume: 0.15,
  _bgmFadeInterval: null,

  // ── Constants ─────────────────────────────
  STATUS_CLR: { 1: '#22c55e', 2: '#f59e0b', 3: '#f43f5e' },
};

// ── Dual-source settings helper ───────────────
// JSONB `config` object first, flat column fallback
const C2S = {
  enablePopupCard: 'enable_popup_card', showPrice: 'show_price', showStatus: 'show_status',
  showSector: 'show_sector', showFacing: 'show_facing', showArea: 'show_area',
  hoverShowSector: 'hover_show_sector', hoverShowArea: 'hover_show_area', hoverShowFacing: 'hover_show_facing',
  enableHdri: 'enable_hdri', enableMusic: 'enable_music', enableSfx: 'enable_sfx',
  enableCompass: 'enable_compass', northAngle: 'north_angle',
  enableHoverPopup: 'enable_hover_popup', showHoverPopup: 'show_hover_popup', enableFloatingLabel: 'enable_floating_label'
};

export function getCfg(k) {
  if (state.config && typeof state.config === 'object' && state.config[k] !== undefined) return state.config[k];
  const snake = C2S[k];
  if (snake && state.uiSettings[snake] !== undefined) return state.uiSettings[snake];
  if (k === 'northAngle') return 0;
  return true; // default: features enabled
}
